import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CASES } from './cases';
import { Reveal } from './Placeholder';
import { Media } from './Media';
import './ProductLanding.css';

/* ─── Scope snapshot: design is the anchor, everything else the supporting bar ─── */
const SCOPE = [
  {
    key: 'design',
    label: 'Design',
    note: 'the spike',
    items: ['Product & interaction', 'UI', 'Motion & animation', 'Prototyping', 'Design systems', 'Figma', 'Specs & eng handoff'],
  },
  { key: 'product', label: 'Product', items: ['Strategy', 'Roadmap', 'Analytics & metrics', 'Prioritization'] },
  { key: 'eng', label: 'Engineering', items: ['Front-end (web)', 'Some iOS (Swift)', 'AI automation & tooling'] },
  { key: 'growth', label: 'Growth & Marketing', items: ['Growth experiments', 'Brand', 'Decks & fundraising', 'Marketing'] },
  { key: 'surfaces', label: 'Surfaces', items: ['Consumer app', 'B2B platform', 'Marketing sites'] },
];

const CRAFT = [
  { t: 'Motion & prototyping', d: 'Interaction is part of the design: kinetic type, timing, the feel of it, not just the frame.', src: '/images/craft/kinetic-type.mp4', poster: '/images/craft/kinetic-type-poster.jpg' },
  { t: 'I build the front-end', d: 'I write code, from creative-coding experiments to shipping the real product UI, so I can iterate in hours, not sprints.', src: '/images/craft/coding-example.png' },
  { t: 'In Figma', d: 'Mockups, components, the messy exploration before the clean screen.', src: '/images/craft/figma-example.png' },
  { t: 'Specs & eng handoff', d: 'I write the tickets. Redlines, states, edge cases: the work that makes it ship right.', src: '/images/craft/ticket-example.png' },
];

function ProductLanding() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* ════════ HERO — words first ════════ */}
      <section className="ppl-hero">
        <div className="pp-container ppl-hero__grid">
          <div className="ppl-hero__text">
            <motion.p
              className="pp-eyebrow ppl-hero__kicker"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              Technical Product Lead · Hands-on Designer
            </motion.p>

            <motion.h1
              className="ppl-hero__headline"
              initial={{ opacity: 0, y: '0.4em' }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            >
              I take products from concept to growth <em>and design every screen along the way.</em>
            </motion.h1>

            <motion.p
              className="ppl-hero__sub"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              For three years I’ve been the sole designer at <strong>Feels Music</strong>, a music tech
              startup, and now I’m its <strong>CPO</strong>. I set the product strategy, design the
              details, and oversee engineering to bring it to life. Previously <strong>Meta</strong>.
            </motion.p>

            <motion.div
              className="ppl-hero__facts"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.65 }}
            >
              {['Sole designer · 3 yrs', 'Intern → CPO', 'Designs · ships · grows', 'Two products'].map((f) => (
                <span key={f} className="ppl-fact">{f}</span>
              ))}
              <a href="#work" className="ppl-hero__scroll">See the work ↓</a>
            </motion.div>
          </div>

          <motion.div
            className="ppl-hero__visual"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <Media
              src="/images/feels-consumer/phone-send.webm"
              poster="/images/feels-consumer/phone-send-poster.png"
              mobileSrc="/images/feels-consumer/phone-send.gif"
              sound
              fit="natural"
              alt="A Feels being created and sent in the Feels app"
            />
          </motion.div>
        </div>
      </section>

      <div className="pp-container"><hr className="pp-rule" /></div>

      {/* ════════ SCOPE SNAPSHOT ════════ */}
      <section className="ppl-scope">
        <div className="pp-container">
          <Reveal className="pp-section-head">
            <div>
              <span className="pp-eyebrow">01 — How I operate</span>
              <h2 className="pp-section-title ppl-scope__title">Effectively a one-person product team.</h2>
            </div>
          </Reveal>

          <div className="ppl-scope__grid">
            {SCOPE.map((col, i) => (
              <Reveal
                key={col.key}
                delay={i * 0.05}
                className={`ppl-scope__col ${col.key === 'design' ? 'ppl-scope__col--anchor' : ''}`}
              >
                <div className="ppl-scope__col-head">
                  <h3 className="ppl-scope__col-label">{col.label}</h3>
                  {col.note && <span className="ppl-scope__col-note">{col.note}</span>}
                </div>
                <ul className="ppl-scope__items">
                  {col.items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              </Reveal>
            ))}
          </div>

          <Reveal className="ppl-scope__statement" delay={0.1}>
            <p>I can hand off. But I can also design it, build it, ship it, measure it, and grow it.</p>
          </Reveal>
        </div>
      </section>

      {/* ════════ FEELS PRIMER ════════ */}
      <section className="ppl-primer">
        <div className="pp-container">
          <Reveal className="ppl-primer__inner">
            <div className="ppl-primer__lead">
              <span className="pp-eyebrow">02 — The company</span>
              <h2 className="ppl-primer__name">Feels</h2>
              <p className="ppl-primer__desc">
                A next-generation music-messaging app. Drop a short music-video moment, like a
                musical GIF, into any chat, so music says what an emoji can’t. Underneath the fun,
                a category shift: music as an active form of connection, and a new discovery and
                revenue channel for artists. I joined as a summer intern, became the company’s only
                designer, and now lead all of product as CPO.
              </p>
            </div>
            <div className="ppl-primer__meta">
              {[
                ['Role', 'Chief Product Officer'],
                ['Arc', 'Intern → Product Lead → CPO'],
                ['Duration', '2023 – Present'],
                ['Scope', 'Strategy · Design · Analytics · AI tooling · Eng roadmap'],
              ].map(([k, v]) => (
                <div key={k} className="ppl-primer__meta-row">
                  <span className="ppl-primer__meta-k">{k}</span>
                  <span className="ppl-primer__meta-v">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════ SELECTED WORK ════════ */}
      <section className="ppl-work" id="work">
        <div className="pp-container">
          <Reveal className="pp-section-head">
            <div>
              <span className="pp-eyebrow">03 — Selected work</span>
              <h2 className="pp-section-title">Three lenses on the products I own.</h2>
            </div>
          </Reveal>

          <div className="ppl-work__list">
            {CASES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.04}>
                <Link to={`/product/${c.slug}`} className="ppl-card">
                  <span className="ppl-card__num">{c.number}</span>
                  <div className="ppl-card__body">
                    <span className="ppl-card__kicker">{c.kicker} · {c.role}</span>
                    <h3 className="ppl-card__title">{c.title}</h3>
                    <p className="ppl-card__line">{c.positioning}</p>
                    <div className="ppl-card__foot">
                      <span className="ppl-card__outcome">
                        <span className="ppl-card__outcome-k">Outcome</span>
                        <span className="ppl-card__outcome-v">{c.cardOutcome}</span>
                      </span>
                      <span className="pp-arrow-link">View case <span className="pp-arrow">→</span></span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ HOW I WORK ════════ */}
      <section className="ppl-craft">
        <div className="pp-container">
          <Reveal className="pp-section-head">
            <div>
              <span className="pp-eyebrow">04 — The craft bar</span>
              <h2 className="pp-section-title">Yes, I actually sit and design. And I love it!</h2>
            </div>
            <p className="ppl-craft__lead">
              Not just direction. The daily Figma iterations, the motion, and the specs that get it
              shipped right, that is the hands-on work behind everything above.
            </p>
          </Reveal>

          <div className="ppl-craft__grid">
            {CRAFT.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.05} className="ppl-craft__item">
                {c.src && <Media src={c.src} poster={c.poster} ratio="9 / 6" fit="cover" alt={c.t} />}
                <h3 className="ppl-craft__t">{c.t}</h3>
                <p className="ppl-craft__d">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ BEYOND FEELS ════════ */}
      <section className="ppl-beyond">
        <div className="pp-container">
          <Reveal className="pp-section-head">
            <div>
              <span className="pp-eyebrow">05 — Beyond Feels</span>
              <h2 className="pp-section-title">The range behind the through-line.</h2>
            </div>
          </Reveal>

          <div className="ppl-beyond__grid">
            <Reveal className="ppl-beyond__block ppl-beyond__block--credential">
              <span className="ppl-beyond__tag">Student startup · 0→1</span>
              <h3 className="ppl-beyond__title">Artesia</h3>
              <div className="ppl-beyond__metarow">
                <span>Product + Eng</span><span>Idea → MVP · 4 mo</span>
              </div>
              <p className="ppl-beyond__body">
                I helped shape the product end to end, strategy, feature ideation, and timelines,
                then led development and shipped it: I developed most of it myself, assisted by two
                engineers (one back-end/SQL, one front-end).
              </p>
              <div className="ppl-beyond__visual">
                <Media src="/images/digital/Artesia_demo.mp4" ratio="16 / 9" fit="cover" alt="Artesia MVP demo" />
              </div>
            </Reveal>

            <Reveal className="ppl-beyond__block ppl-beyond__block--credential" delay={0.06}>
              <span className="ppl-beyond__tag">FinTech · Ads &amp; Business</span>
              <h3 className="ppl-beyond__title">Meta</h3>
              <div className="ppl-beyond__metarow">
                <span>Design Intern</span><span>Summer 2023</span>
              </div>
              <p className="ppl-beyond__body">
                On Meta’s FinTech team I designed Payout Onboarding, Education &amp; Support:
                competitor and internal audits, four flows taken from ideation through error states
                and iteration to finalized UXR and the content-guidance handoff, plus a stretch
                project on severity notifications for tax concerns. What stuck with me: designing
                within tight, specific parameters and no large design system, where creativity and
                product thinking still have to carry the work.
              </p>
              <div className="ppl-beyond__visual">
                <Media src="/images/beyond/meta-guidance.png" ratio="16 / 9" fit="cover" alt="Meta Payout Onboarding flows and content-guidance documentation" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ════════ CTA ════════ */}
      <section className="ppl-cta">
        <div className="pp-container pp-container--narrow">
          <Reveal className="ppl-cta__inner">
            <h2 className="ppl-cta__title">Let’s build something.</h2>
            <p className="ppl-cta__body">
              I’m looking for founding / lead design roles at startups where I can go deep on a
              problem and own the outcome, ideally in music, AI, or consumer. Based in LA, open to NYC.
            </p>
            <div className="ppl-cta__actions">
              <a href="mailto:sami@samidenicola.com" className="pp-btn pp-btn--solid">sami@samidenicola.com</a>
              <a href="https://www.linkedin.com/in/samidenicola/" target="_blank" rel="noopener noreferrer" className="pp-btn pp-btn--ghost">LinkedIn ↗</a>
            </div>
          </Reveal>
        </div>
      </section>
    </motion.div>
  );
}

export default ProductLanding;
